import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronDown } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import NewsFeed from './NewsFeed';
import PageSpace from './PageSpace';
import LauncherSpace from './LauncherSpace';
import WikiSpace from './WikiSpace';
import SpaceDescription from './SpaceDescription';
import AppGallery from '../AppGallery';
import AdminMenu from '../../Admin/AdminMenu';
import { useSpaces } from '../../../contexts/SpacesContext';
import { SPACE_TYPES } from '../../../utils/api/spaces';

/**
 * Get Lucide icon component by name (kebab-case to PascalCase)
 */
const getIconComponent = (iconName) => {
  if (!iconName) return LucideIcons.LayoutGrid;
  
  // Convert kebab-case to PascalCase
  const pascalCase = iconName
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
  
  return LucideIcons[pascalCase] || LucideIcons.LayoutGrid;
};

const TabbedInterface = ({
  isVisible,
  onClose,
  launchApp,
  apps = [],
  loading,
  error,
  onVersionSwitch,
  theme = 'dark',
  initialTab = null,
  launchSDK
}) => {
  const { spaces, loading: spacesLoading, activeSpaceId, setActiveSpaceId } = useSpaces();
  
  // Filter to only visible spaces (not hidden), sorted by display_order
  const visibleSpaces = useMemo(() => {
    return spaces
      .filter(s => !s.is_hidden)
      .sort((a, b) => a.display_order - b.display_order);
  }, [spaces]);

  // Set initial active space
  useEffect(() => {
    if (visibleSpaces.length > 0 && !activeSpaceId) {
      // If initialTab provided, find matching space
      if (initialTab) {
        const matchingSpace = visibleSpaces.find(
          s => s.name.toLowerCase() === initialTab.toLowerCase()
        );
        if (matchingSpace) {
          setActiveSpaceId(matchingSpace.space_id);
          return;
        }
      }
      // Default to first space
      setActiveSpaceId(visibleSpaces[0].space_id);
    }
  }, [visibleSpaces, activeSpaceId, initialTab, setActiveSpaceId]);

  // Get active space object
  const activeSpace = useMemo(() => {
    return visibleSpaces.find(s => s.space_id === activeSpaceId);
  }, [visibleSpaces, activeSpaceId]);

  if (!isVisible) return null;

  // Show loading while spaces are being fetched
  if (spacesLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-gray-900/95 backdrop-blur-md border border-gray-700/50 rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
          <p className="text-white mt-4">Loading spaces...</p>
        </div>
      </div>
    );
  }

  // Fallback if no spaces exist
  if (visibleSpaces.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-gray-900/95 backdrop-blur-md border border-gray-700/50 rounded-lg p-8 text-center">
          <p className="text-white mb-4">No spaces available.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center md:bg-black md:bg-opacity-50">
      {/* Mobile: full screen with dvh for browser bar, Desktop: 95% viewport */}
      <div 
        className="bg-gray-900/95 backdrop-blur-md md:border md:border-gray-700/50 md:rounded-lg shadow-xl overflow-hidden 
                   w-screen h-[100dvh] supports-[height:100dvh]:h-[100dvh] 
                   md:w-[95vw] md:h-[95vh]">
        
        {/* Header with dynamic tabs (desktop) / dropdown (mobile) */}
        <div className="bg-gray-800/80 border-b border-gray-700/50 px-4 md:px-6 py-3 flex items-center justify-between">
          
          {/* Mobile: Dropdown navigation */}
          <div className="md:hidden flex-1">
            <div className="relative">
              <select
                value={activeSpaceId || ''}
                onChange={(e) => setActiveSpaceId(Number(e.target.value))}
                className="w-full appearance-none bg-gray-700/50 border border-gray-600 rounded-lg 
                           px-4 py-2.5 pr-10 text-white font-medium
                           focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                           cursor-pointer"
              >
                {visibleSpaces.map((space) => (
                  <option key={space.space_id} value={space.space_id}>
                    {space.name}
                  </option>
                ))}
              </select>
              <ChevronDown 
                size={18} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" 
              />
            </div>
          </div>

          {/* Desktop: Tab navigation */}
          <div className="hidden md:flex items-center overflow-x-auto scrollbar-hide">
            {visibleSpaces.map((space) => {
              const Icon = getIconComponent(space.icon);
              const isActive = activeSpaceId === space.space_id;
              
              return (
                <button
                  key={space.space_id}
                  onClick={() => setActiveSpaceId(space.space_id)}
                  className={`
                    flex items-center space-x-2 px-6 py-3 transition-all duration-200 border-b-2 whitespace-nowrap
                    ${isActive 
                      ? 'text-white border-blue-500 bg-gray-700/50' 
                      : 'text-gray-400 border-transparent hover:text-white hover:bg-gray-700/30'
                    }
                  `}
                >
                  <Icon size={16} />
                  <span className="font-medium">{space.name}</span>
                </button>
              );
            })}
          </div>
          
          {/* Right side: Admin Menu + Close button */}
          <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
            <AdminMenu theme="dark" onLaunchSDK={launchSDK} />
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tab content */}
        <div className="h-full flex flex-col" style={{ height: 'calc(100% - 60px)' }}>
          {activeSpace && (
            <>
              {/* Optional space description */}
              {activeSpace.description && (
                <SpaceDescription description={activeSpace.description} />
              )}
              
              {/* Content container - takes remaining height */}
              <div className="flex-1 min-h-0">
                {/* Render content based on space type */}
                {activeSpace.space_type === SPACE_TYPES.NEWSFEED && (
                  <NewsFeed theme={theme} config={activeSpace.config} />
                )}
                
                {activeSpace.space_type === SPACE_TYPES.APPS && (
                  <AppGallery
                    isVisible={true}
                    onClose={() => {}}
                    launchApp={launchApp}
                    apps={apps}
                    loading={loading}
                    error={error}
                    onVersionSwitch={onVersionSwitch}
                    isEmbedded={true}
                    spaceId={activeSpace.space_id}
                    isDefaultSpace={activeSpace.is_default}
                    launchSDK={launchSDK}
                  />
                )}
                
                {activeSpace.space_type === SPACE_TYPES.PAGE && (
                  <PageSpace 
                    space={activeSpace}
                    theme={theme}
                  />
                )}

                {activeSpace.space_type === SPACE_TYPES.LAUNCHER && (
                  <LauncherSpace 
                    space={activeSpace}
                    theme={theme}
                  />
                )}

                {activeSpace.space_type === SPACE_TYPES.WIKI && (
                  <WikiSpace 
                    space={activeSpace}
                    theme={theme}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TabbedInterface;
